/* eslint-disable react-refresh/only-export-components */
import type {ChangeEvent, ReactElement} from 'react';
import {useContext, useState} from 'react';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {setComponentStyle, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {StepButtonsDescriptor} from './step-buttons-control.types';
import {DECIMAL_RADIX, DEFAULT_STEP, DEFAULT_VALUE} from './step-buttons-control.types';
import styles from './step-buttons-control.module.scss';

interface StepButtonsControlInnerProps extends EmailMakerControlProps {
  descriptor: StepButtonsDescriptor;
}

function StepButtonsControlInner({component, descriptor}: StepButtonsControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const step = descriptor.step ?? DEFAULT_STEP;
  const min = descriptor.min;
  const max = descriptor.max;
  const defaults = descriptor.defaults ?? DEFAULT_VALUE;

  const rawValue = useStyleProperty(editor, component, descriptor.property);

  const parsedValue = rawValue !== '' ? parseInt(rawValue, DECIMAL_RADIX) : defaults;
  const numericValue = isNaN(parsedValue) ? defaults : parsedValue;

  const [isFocused, setIsFocused] = useState(false);

  function clamp(val: number): number {
    return Math.min(max, Math.max(min, val));
  }

  function writeValue(val: number): void {
    if (!editor) {
      return;
    }
    const clamped = clamp(val);
    setComponentStyle(editor, {
      property: descriptor.property,
      value: `${clamped}${descriptor.unit}`,
      component,
    });
    if (descriptor.onValueChange) {
      descriptor.onValueChange(component, editor, clamped);
    }
  }

  function handleStepDown(): void {
    writeValue(numericValue - step);
  }

  function handleStepUp(): void {
    writeValue(numericValue + step);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>): void {
    const parsed = parseInt(e.target.value, DECIMAL_RADIX);
    if (!isNaN(parsed)) {
      writeValue(parsed);
    }
  }

  const isAtMin = numericValue <= min;
  const isAtMax = numericValue >= max;

  const renderUnit = (): ReactElement | null => {
    if (!descriptor.unit) {
      return null;
    }
    return <Text val={descriptor.unit} size={TextSize.MD} className={styles.unit} />;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelRow}>
        <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} className={styles.label} />
        <div className={`${styles.inputContainer} ${isFocused ? styles.inputContainerFocused : ''}`}>
          <div className={`${styles.stepBtn} ${styles.stepBtnDown} ${isAtMin ? styles.stepBtnDisabled : ''}`}>
            <Icon ariaLabel={window.i18next.t('pmwjs_decrement')} icon="icon-minus" isDisabled={isAtMin} isUsedAsButton onClick={handleStepDown} size={IconSize.SIZE_ICON_16} />
          </div>
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              max={max}
              min={min}
              onBlur={(): void => {
                setIsFocused(false);
              }}
              onChange={handleInputChange}
              onFocus={(): void => {
                setIsFocused(true);
              }}
              step={step}
              type="number"
              value={numericValue}
            />
            {renderUnit()}
          </div>
          <div className={`${styles.stepBtn} ${styles.stepBtnUp} ${isAtMax ? styles.stepBtnDisabled : ''}`}>
            <Icon ariaLabel={window.i18next.t('pmwjs_increment')} icon="icon-plus" isDisabled={isAtMax} isUsedAsButton onClick={handleStepUp} size={IconSize.SIZE_ICON_16} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function makeStepButtonsControl(descriptor: StepButtonsDescriptor): EmailMakerControl {
  return createDescriptorControl(StepButtonsControlInner, descriptor);
}
